<template>
  <div class="workspace-document-detail-view">
    <div class="title-panel">
      <h2>{{ t('documentDetailView.document') }}</h2>
      <h1>
        <template v-if="documentName">
          {{ documentName }}
        </template>
        <PVSkeleton v-else width="50%" height="1em" />
      </h1>
    </div>

    <div class="workspace-document-detail-view__content">
      <div class="workspace-document-detail-view__metadata">
        <workspace-document-status-indicator
          v-if="isWorkspaceDocument && workflowStatus"
          class="workspace-document-detail-view__status-indicator"
          :workspace-id="workspaceId"
          :document-id="documentId"
          :workflow-status="workflowStatus"
          :can-edit-document="canEditMetadata"
          @status-updated="handleStatusUpdated"
        />

        <loading-wrapper
          :is-loading="isLoadingMetadataPanel"
          positioning="flex-center"
        >
          <workspace-document-metadata
            v-if="isWorkspaceDocument"
            :workspace-id="workspaceId"
            :document-id="documentId"
            :can-edit-metadata="canEditWorkspaceDocumentMetadata"
            @metadata-loaded="handleMetadataLoaded"
          />
          <read-only-workspace-document-metadata
            v-else
            :workspace-id="workspaceId"
            :document-id="documentId"
            @metadata-loaded="handleMetadataLoaded"
          />
        </loading-wrapper>
      </div>

      <div class="workspace-document-detail-view__preview">
        <adobe-pdf-viewer
          ref="adobeViewerRef"
          :file-endpoint="endpoints.file"
          @loaded="handlePreviewLoaded"
          @error="handlePreviewError"
        />
      </div>
    </div>

    <div class="workspace-document-detail-view__actions">
      <vf-button
        v-if="isEditable"
        data-testid="workspace-document-update-button"
        :size="ButtonSize.LARGE"
        :variant="ButtonVariant.TERTIARY"
        @click="showUpdateModal"
      >
        {{ t('documentDetailView.updateDocument') }}
        <font-awesome-icon :icon="['fas', 'upload']" />
      </vf-button>
      <a
        :download="isPdf ? null : filename"
        :href="fileForDownload ?? undefined"
        target="_blank"
      >
        <vf-button
          :disabled="!fileForDownload"
          :size="ButtonSize.LARGE"
          :variant="ButtonVariant.SECONDARY"
        >
          {{
            isPdf
              ? t('documentDetailView.openInNewTab')
              : t('documentDetailView.downloadDocument')
          }}
          <font-awesome-icon
            v-if="isPdf"
            :icon="['fas', 'arrow-up-right-from-square']"
          />
          <font-awesome-icon v-else :icon="['fas', 'download']" />
        </vf-button>
      </a>
      <vf-button
        v-if="hasRenditionFile"
        data-testid="workspace-document-download-rendition-button"
        :size="ButtonSize.LARGE"
        :variant="ButtonVariant.SECONDARY"
        @click="downloadRenditionFile"
      >
        {{ t('documentDetailView.downloadRenditionFile') }}
        <font-awesome-icon :icon="['fas', 'download']" />
      </vf-button>
    </div>

    <workspace-document-update-modal
      v-if="isEditable"
      ref="updateModalRef"
      :workspace-id="workspaceId"
      :document-id="documentId"
      @updated="handleDocumentUpdated"
    />
  </div>
</template>

<script setup lang="ts">
  import ReadOnlyWorkspaceDocumentMetadata from '@/components/DocumentsMetadata/ReadOnlyWorkspaceDocumentMetadata.vue';
  import WorkspaceDocumentStatusIndicator from '@/components/WorkspaceDocumentStatusIndicator/WorkspaceDocumentStatusIndicator.vue';
  import { useToast } from '@/composables/useToast';
  import {
    endpointsList,
    getWorkspaceDocumentDetailEndpoints,
  } from '@/helpers';
  import WorkspaceDocumentMetadata from '@/components/DocumentsMetadata/WorkspaceDocumentMetadata.vue';
  import WorkspaceDocumentUpdateModal from '@/components/WorkspaceDocumentUpdateModal/WorkspaceDocumentUpdateModal.vue';
  import VfButton from '@/components/VfButton/VfButton.vue';
  import {
    ButtonSize,
    ButtonVariant,
  } from '@/components/VfButton/VfButton.types';
  import {
    IWorkspaceDocumentUpdateInfoDto,
    TITLE_METADATA_FIELD,
    WorkflowStatus,
  } from 'types';
  import axios from 'axios';
  import {
    computed,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { DocumentsMetadataResponse } from '@/components/DocumentsMetadata/DocumentsMetadata.types';
  import { WorkspaceDocumentDetailAccessMode } from '@/helpers';
  import { useRoles } from '@/composables/useRoles';
  import AdobePdfViewer from '@/components/AdobePdfViewer/AdobePdfViewer.vue';

  const props = defineProps<{
    workspaceId: string;
    documentId: string;
    accessMode?: WorkspaceDocumentDetailAccessMode;
  }>();

  const { t } = useI18n();
  const { createErrorToast } = useToast();
  const { canManageWorkspaceDocuments } = useRoles();

  const metadataResponse = ref<DocumentsMetadataResponse | null>(null);
  const fileForDownload = ref<string | null>(null);
  const filename = ref<string | undefined>(undefined);
  const isPdf = ref(false);
  const hasRenditionFile = ref(false);
  const workflowStatus = ref<WorkflowStatus | null>(null);
  const canEditMetadata = ref(true);
  const isResolvingEditAccess = ref(true);
  const isLoadingDetailInfo = ref(true);
  const updateModalRef = ref<InstanceType<
    typeof WorkspaceDocumentUpdateModal
  > | null>(null);
  const adobeViewerRef = ref<InstanceType<typeof AdobePdfViewer> | null>(null);

  const isWorkspaceDocument = computed(() => props.accessMode !== 'readOnly');
  const canEditWorkspaceDocumentMetadata = computed(() => {
    return (
      isWorkspaceDocument.value &&
      canEditMetadata.value &&
      workflowStatus.value === WorkflowStatus.IN_PROGRESS
    );
  });
  const isEditable = computed(() => canEditWorkspaceDocumentMetadata.value);
  const isLoadingMetadataPanel = computed(() => {
    return (
      isResolvingEditAccess.value ||
      (isWorkspaceDocument.value && isLoadingDetailInfo.value)
    );
  });
  const endpoints = computed(() =>
    getWorkspaceDocumentDetailEndpoints(
      props.accessMode ?? 'editable',
      props.workspaceId,
      props.documentId,
    ),
  );

  const documentName = computed(() => {
    const nameField = metadataResponse.value?.metadata.find(
      (field) => field.fieldName === TITLE_METADATA_FIELD,
    );
    return nameField?.fieldValue;
  });

  const renditionFileDownloadUrl = computed(() => {
    return `${window.location.origin}/api/${endpoints.value.renditionFile}`;
  });

  async function loadDetailInfo(): Promise<void> {
    try {
      const response = await axios.get<IWorkspaceDocumentUpdateInfoDto>(
        endpoints.value.detailInfo,
      );
      hasRenditionFile.value = response.data.hasRenditionFile;
      workflowStatus.value = response.data.workflowStatus;
    } catch {
      createErrorToast(t('documentDetailView.errors.cannotLoadUpdateInfo'));
    } finally {
      isLoadingDetailInfo.value = false;
    }
  }

  async function resolveAccessMode(): Promise<void> {
    if (!isWorkspaceDocument.value || !canManageWorkspaceDocuments.value) {
      canEditMetadata.value = false;
      isResolvingEditAccess.value = false;
      return;
    }

    try {
      const response = await axios.get<boolean>(
        endpointsList.workspaces.canEditWorkspaceDocument(
          props.workspaceId,
          props.documentId,
        ),
      );
      canEditMetadata.value = response.data;
    } catch {
      createErrorToast(t('documentDetailView.errors.cannotLoadUpdateInfo'));
      canEditMetadata.value = props.accessMode !== 'readOnly';
    } finally {
      isResolvingEditAccess.value = false;
    }
  }

  function handlePreviewLoaded(
    loadedFilename: string | undefined,
    loadedIsPdf: boolean,
    fileUrl: string,
  ): void {
    filename.value = loadedFilename;
    isPdf.value = loadedIsPdf;
    if (fileForDownload.value) {
      URL.revokeObjectURL(fileForDownload.value);
    }
    fileForDownload.value = fileUrl;
  }

  function handlePreviewError(): void {
    createErrorToast(t('documentDetailView.errors.cannotLoadUpdateInfo'));
  }

  function handleMetadataLoaded(metadata: DocumentsMetadataResponse): void {
    metadataResponse.value = metadata;
  }

  function showUpdateModal(): void {
    void updateModalRef.value?.show();
  }

  function downloadRenditionFile(): void {
    window.open(renditionFileDownloadUrl.value, '_blank');
  }

  function handleDocumentUpdated(): void {
    void adobeViewerRef.value?.init();
  }

  function handleStatusUpdated(): void {
    void loadDetailInfo();
  }

  onBeforeUnmount(() => {
    if (fileForDownload.value) {
      URL.revokeObjectURL(fileForDownload.value);
    }
  });

  watch(
    () => [isWorkspaceDocument.value, canManageWorkspaceDocuments.value],
    () => {
      void resolveAccessMode();
    },
  );

  onMounted(async () => {
    await nextTick(async () => {
      await resolveAccessMode();
      await loadDetailInfo();
    });
  });
</script>

<style scoped lang="scss">
  .workspace-document-detail-view {
    .title-panel {
      margin-bottom: 50px;

      h2 {
        font-size: $subheading-font-size;
      }

      h1 {
        font-size: 50px;
      }
    }

    &__content {
      display: grid;
      grid-template-columns: 1fr 1fr;
      border-bottom: 1px solid $border-color;

      @media (max-width: 900px) {
        grid-template-columns: 1fr;
      }
    }

    &__metadata {
      padding: 10px;
      border-right: 1px solid $border-color;
      overflow-x: hidden;

      @media (max-width: 900px) {
        border-right: none;
        border-bottom: 1px solid $border-color;
        margin-top: 1.5rem;
      }
    }

    &__status-indicator {
      margin-bottom: 1.5rem;
    }

    &__preview {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      min-height: 1000px;
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      column-gap: 1rem;

      button {
        margin-top: 20px;
        align-self: flex-end;
        display: flex;
        align-items: center;
        column-gap: 1rem;
      }

      a {
        text-decoration: none;
      }
    }
  }
</style>
